<script setup lang="ts">
import { useFlag } from '@unleash/proxy-client-vue'
import { type Component, computed } from 'vue'

import AdvertisersIcon from '@/assets/icons/advertisers.svg?component'
import CampaignsIcon from '@/assets/icons/campaigns.svg?component'
import ClientsIcon from '@/assets/icons/clients.svg?component'
import CreatorsMediaIcon from '@/assets/icons/creators-media.svg?component'
import CreatorsIcon from '@/assets/icons/creators.svg?component'
import DashboardIcon from '@/assets/icons/dashboard.svg?component'
import DocumentationIcon from '@/assets/icons/documentation.svg?component'
import ExternalLinkIcon from '@/assets/icons/external-link.svg?component'
import LogoutIcon from '@/assets/icons/logout.svg?component'
import PaymentsIcon from '@/assets/icons/payments.svg?component'
import PowerBiIcon from '@/assets/icons/power-bi.svg?component'
import PushIcon from '@/assets/icons/push.svg?component'
import RedirectPostsIcon from '@/assets/icons/redirect-posts.svg?component'
import ReportsIcon from '@/assets/icons/reports.svg?component'
import SegmentsIcon from '@/assets/icons/segments.svg?component'
import { FLAG_MARKETING_NOTIFICATIONS } from '@/lib/constants/flags.constants'
import { config } from '@/lib/helpers'
import { BoxComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const isMarketingNotificationsEnabled = useFlag(FLAG_MARKETING_NOTIFICATIONS)

const menuItems = computed<{ title: string; route: string; icon: Component; isExternal?: boolean; isNewWindow?: boolean }[]>(() => [
  {
    title: 'Dashboard',
    route: `${config.legacyAdminAppUrl}/`,
    icon: DashboardIcon,
    isExternal: true,
  },
  {
    title: 'Advertisers',
    route: `${config.legacyAdminAppUrl}/#/stores`,
    icon: AdvertisersIcon,
    isExternal: true,
  },
  {
    title: 'Creators',
    route: '/creators',
    icon: CreatorsIcon,
  },
  {
    title: 'Segments',
    route: '/segments',
    icon: SegmentsIcon,
  },
  {
    title: 'Clients',
    route: '/clients',
    icon: ClientsIcon,
  },
  {
    title: 'Campaigns',
    route: '/campaigns',
    icon: CampaignsIcon,
  },
  {
    title: 'Power BI',
    route: config.powerBiUrl,
    icon: PowerBiIcon,
    isExternal: true,
    isNewWindow: true,
  },
  {
    title: 'Content library',
    route: '/content-library',
    icon: CreatorsMediaIcon,
  },
  {
    title: 'Payments',
    route: `${config.legacyAdminAppUrl}/#/payments`,
    icon: PaymentsIcon,
    isExternal: true,
  },
  {
    title: 'Return ads',
    route: '/return-ads',
    icon: RedirectPostsIcon,
  },
  ...(isMarketingNotificationsEnabled.value
    ? [{ title: 'Marketing notifications', route: '/marketing-notifications', icon: PushIcon }]
    : []),
  {
    title: 'Reports',
    route: `${config.legacyAdminAppUrl}/#/reports`,
    icon: ReportsIcon,
    isExternal: true,
  },
  {
    title: 'Documentation',
    route: 'https://metapic.getoutline.com',
    icon: DocumentationIcon,
    isExternal: true,
    isNewWindow: true,
  },
])
</script>
<template>
  <FlexComponent direction="column" class="sidebar-container">
    <FlexComponent class="sidebar-container__i" direction="column" justify="space-between">
      <BoxComponent :pl="8" :pr="8" :pt="16">
        <FlexComponent direction="column" grow="1">
          <template v-for="item in menuItems" :key="item.route">
            <a v-if="item.isExternal" :href="item.route" class="link" :target="item.isNewWindow ? '_blank' : undefined">
              <BoxComponent :p="20">
                <FlexComponent align="center" justify="space-between">
                  <FlexComponent :gap="12" align="center">
                    <el-icon :size="24">
                      <component :is="item.icon" />
                    </el-icon>
                    <TypographyComponent variant="title-medium" color="#fff" class="link__title">{{ item.title }}</TypographyComponent>
                  </FlexComponent>
                  <ExternalLinkIcon v-if="item.isNewWindow" class="link__external-icon" />
                </FlexComponent>
              </BoxComponent>
            </a>
            <router-link v-else :to="item.route" class="link" active-class="active">
              <BoxComponent :p="20">
                <FlexComponent :gap="12" align="center">
                  <el-icon :size="24">
                    <component :is="item.icon" />
                  </el-icon>
                  <TypographyComponent variant="title-medium" color="#fff" class="link__title">{{ item.title }}</TypographyComponent>
                </FlexComponent>
              </BoxComponent>
            </router-link>
          </template>
        </FlexComponent>
      </BoxComponent>

      <BoxComponent :p="8">
        <router-link to="/logout">
          <BoxComponent :p="20">
            <FlexComponent :gap="12" align="center">
              <el-icon :size="24">
                <LogoutIcon />
              </el-icon>
              <TypographyComponent variant="title-medium" color="#fff" class="link__title"> Logout </TypographyComponent>
            </FlexComponent>
          </BoxComponent>
        </router-link>
      </BoxComponent>
    </FlexComponent>
  </FlexComponent>
</template>
<style scoped>
.sidebar-container {
  position: fixed;
  left: 0;
  top: 72px;
  width: 80px;
  height: calc(100vh - 72px);
  box-shadow: 2px 0 16px 0 var(--button-hover);
  z-index: var(--z-index-9);
  transition: width ease 0.2s;

  &:hover {
    width: 260px;

    .link__title {
      display: initial;
    }
  }

  &:before {
    content: '';
    position: absolute;
    height: 48px;
    width: 24px;
    top: 0;
    right: -24px;

    border-top-left-radius: 24px;
    background-color: transparent;
    box-shadow: 0 -24px 0 0 var(--primary);
  }
}

.sidebar-container__i {
  flex: 1;
  overflow: auto;
  background-color: var(--primary);
}

.link {
  position: relative;
  border-radius: 8px;
  white-space: nowrap;
  transition: background var(--transition) ease;

  &.active,
  &:hover {
    background: var(--navigation-background);
  }

  &.active {
    &:before {
      content: '';
      position: absolute;
      left: -8px;
      width: 4px;
      height: 64px;
      border-radius: 4px;
      background-color: var(--inverted);
    }
  }
}

.link__title {
  display: none;
}

.link__external-icon {
  opacity: 0.32;
}
</style>
